import type {
  Board,
  ImportResult,
  Item,
  ItemInput,
  Outfit,
  OutfitInput,
  PagePayload,
  PricePoint,
  User,
} from '@closetly/core';
import { onlineManager, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { ClientResponseError } from 'pocketbase';
import { useEffect } from 'react';
import { pb } from './pb';

export const itemsKey = (uid: string) => ['items', uid] as const;
export const outfitsKey = (uid: string) => ['outfits', uid] as const;
export const boardsKey = (uid: string) => ['boards', uid] as const;
export const meKey = (uid: string) => ['me', uid] as const;
export const historyKey = (itemId: string) => ['price_history', itemId] as const;

export function useItems(uid: string) {
  return useQuery({
    queryKey: itemsKey(uid),
    queryFn: () => pb.collection('items').getFullList<Item>({ sort: '-created' }),
  });
}

export function useMe(user: User) {
  return useQuery({
    queryKey: meKey(user.id),
    queryFn: () => pb.collection('users').getOne<User>(user.id),
    placeholderData: user,
  });
}

export function useOutfits(uid: string) {
  return useQuery({
    queryKey: outfitsKey(uid),
    queryFn: () => pb.collection('outfits').getFullList<Outfit>({ sort: '-created' }),
  });
}

export function useBoards(uid: string) {
  return useQuery({
    queryKey: boardsKey(uid),
    queryFn: () => pb.collection('boards').getFullList<Board>({ sort: '-created' }),
  });
}

/** Keeps items, outfits and boards in sync with other devices and with images stored by the server. */
export function useRealtime(uid: string) {
  const qc = useQueryClient();
  useEffect(() => {
    const unsubs: (() => void)[] = [];
    let cancelled = false;
    for (const [collection, key] of [
      ['items', itemsKey(uid)],
      ['outfits', outfitsKey(uid)],
      ['boards', boardsKey(uid)],
    ] as const) {
      pb.collection(collection)
        .subscribe('*', () => qc.invalidateQueries({ queryKey: key }))
        .then((u) => {
          if (cancelled) u();
          else unsubs.push(u);
        })
        .catch(() => {
          // offline or not allowed; the next refetch catches up
        });
    }
    return () => {
      cancelled = true;
      unsubs.forEach((u) => u());
    };
  }, [qc, uid]);
}

export function importLink(url: string, text = '') {
  return pb.send<ImportResult>('/api/closetly/import', { method: 'POST', body: { url, text } });
}

/** Price changes the server recorded while watching a piece, oldest first. */
export function usePriceHistory(itemId: string | null) {
  return useQuery({
    queryKey: historyKey(itemId ?? ''),
    queryFn: () =>
      pb.collection('price_history').getFullList<PricePoint>({
        filter: pb.filter('item = {:id}', { id: itemId }),
        sort: 'checked_at',
        fields: 'price_cents,available,checked_at',
      }),
    enabled: !!itemId,
  });
}

export interface PriceCheck {
  price_cents?: number;
  error?: string;
  event?: string;
  notified: number;
}

/** "Jetzt prüfen": the server reads the shop page right away. */
export function checkPrice(itemId: string) {
  return pb.send<PriceCheck>(`/api/closetly/prices/check/${itemId}`, { method: 'POST' });
}

/** Product data the browser button collected on a shop page. */
export function importPage(page: PagePayload) {
  return pb.send<ImportResult>('/api/closetly/import-page', { method: 'POST', body: page });
}

// ---- mutations ---------------------------------------------------------------
//
// Mutations made offline are paused, kept in IndexedDB together with the
// cache and resumed after a restart. A resumed mutation only knows its
// variables, so the functions are registered here by key rather than passed
// to useMutation. All writes share one scope and run one after another, so a
// piece created offline exists before an outfit that lists it is saved.

/** `id` updates an item; `createId` creates one under that id (see newRecordId). */
export type SaveItemVars = { id?: string; createId?: string; owner: string; data: Partial<ItemInput> };
type DeleteItemVars = { id: string; owner: string };
type BudgetVars = { uid: string; budget_cents: number };
type Snapshot = Item[] | undefined;

/** `id` updates an outfit; `createId` creates one; add/removeItem change one piece without resending the list. */
export type SaveOutfitVars = {
  id?: string;
  createId?: string;
  owner: string;
  data?: Partial<OutfitInput>;
  addItem?: string;
  removeItem?: string;
};
type DeleteOutfitVars = { id: string; owner: string };

/** Like SaveOutfitVars: `add`/`remove` save or unsave single pieces and outfits. */
export type SaveBoardVars = {
  id?: string;
  createId?: string;
  owner: string;
  name?: string;
  add?: { items?: string[]; outfits?: string[] };
  remove?: { items?: string[]; outfits?: string[] };
};
type DeleteBoardVars = { id: string; owner: string };

/** PocketBase's `field+` / `field-` modifiers change relations without a read first. */
function relationChanges(add?: { [k: string]: string[] | undefined }, remove?: { [k: string]: string[] | undefined }) {
  const body: Record<string, string[]> = {};
  for (const [k, v] of Object.entries(add ?? {})) if (v?.length) body[`${k}+`] = v;
  for (const [k, v] of Object.entries(remove ?? {})) if (v?.length) body[`${k}-`] = v;
  return body;
}

const saveKey = ['items', 'save'] as const;
const deleteKey = ['items', 'delete'] as const;
const budgetKey = ['me', 'budget'] as const;
const saveOutfitKey = ['outfits', 'save'] as const;
const deleteOutfitKey = ['outfits', 'delete'] as const;
const saveBoardKey = ['boards', 'save'] as const;
const deleteBoardKey = ['boards', 'delete'] as const;
const scope = { id: 'closetly-writes' };

const isDuplicateId = (err: unknown) =>
  err instanceof ClientResponseError && err.response?.data?.id?.code === 'validation_not_unique';
const isGone = (err: unknown) => err instanceof ClientResponseError && err.status === 404;

export function registerMutations(qc: QueryClient) {
  qc.setMutationDefaults<Item, Error, SaveItemVars>(saveKey, {
    scope,
    mutationFn: async ({ id, createId, owner, data }) => {
      if (id) return pb.collection('items').update<Item>(id, data);
      try {
        return await pb.collection('items').create<Item>({ ...data, id: createId, owner });
      } catch (err) {
        // sent once already, before the app was closed
        if (createId && isDuplicateId(err)) return pb.collection('items').getOne<Item>(createId);
        throw err;
      }
    },
    onSettled: (_d, _e, { owner }) => qc.invalidateQueries({ queryKey: itemsKey(owner) }),
  });
  qc.setMutationDefaults<boolean, Error, DeleteItemVars>(deleteKey, {
    scope,
    mutationFn: async ({ id }) => {
      try {
        return await pb.collection('items').delete(id);
      } catch (err) {
        if (isGone(err)) return true;
        throw err;
      }
    },
    // the server also drops the piece from its outfits and boards
    onSettled: (_d, _e, { owner }) =>
      Promise.all([
        qc.invalidateQueries({ queryKey: itemsKey(owner) }),
        qc.invalidateQueries({ queryKey: outfitsKey(owner) }),
        qc.invalidateQueries({ queryKey: boardsKey(owner) }),
      ]),
  });
  qc.setMutationDefaults<Outfit, Error, SaveOutfitVars>(saveOutfitKey, {
    scope,
    mutationFn: async ({ id, createId, owner, data, addItem, removeItem }) => {
      const body = { ...data, ...relationChanges({ items: addItem ? [addItem] : [] }, { items: removeItem ? [removeItem] : [] }) };
      if (id) return pb.collection('outfits').update<Outfit>(id, body);
      try {
        return await pb.collection('outfits').create<Outfit>({ ...body, id: createId, owner });
      } catch (err) {
        if (createId && isDuplicateId(err)) return pb.collection('outfits').getOne<Outfit>(createId);
        throw err;
      }
    },
    onSettled: (_d, _e, { owner }) => qc.invalidateQueries({ queryKey: outfitsKey(owner) }),
  });
  qc.setMutationDefaults<boolean, Error, DeleteOutfitVars>(deleteOutfitKey, {
    scope,
    mutationFn: async ({ id }) => {
      try {
        return await pb.collection('outfits').delete(id);
      } catch (err) {
        if (isGone(err)) return true;
        throw err;
      }
    },
    onSettled: (_d, _e, { owner }) =>
      Promise.all([
        qc.invalidateQueries({ queryKey: outfitsKey(owner) }),
        qc.invalidateQueries({ queryKey: boardsKey(owner) }),
      ]),
  });
  qc.setMutationDefaults<Board, Error, SaveBoardVars>(saveBoardKey, {
    scope,
    mutationFn: async ({ id, createId, owner, name, add, remove }) => {
      const body = { ...(name !== undefined ? { name } : {}), ...relationChanges(add, remove) };
      if (id) return pb.collection('boards').update<Board>(id, body);
      try {
        return await pb.collection('boards').create<Board>({
          id: createId,
          owner,
          name,
          items: add?.items ?? [],
          outfits: add?.outfits ?? [],
        });
      } catch (err) {
        if (createId && isDuplicateId(err)) return pb.collection('boards').getOne<Board>(createId);
        throw err;
      }
    },
    onSettled: (_d, _e, { owner }) => qc.invalidateQueries({ queryKey: boardsKey(owner) }),
  });
  qc.setMutationDefaults<boolean, Error, DeleteBoardVars>(deleteBoardKey, {
    scope,
    mutationFn: async ({ id }) => {
      try {
        return await pb.collection('boards').delete(id);
      } catch (err) {
        if (isGone(err)) return true;
        throw err;
      }
    },
    onSettled: (_d, _e, { owner }) => qc.invalidateQueries({ queryKey: boardsKey(owner) }),
  });
  qc.setMutationDefaults<User, Error, BudgetVars>(budgetKey, {
    scope,
    mutationFn: ({ uid, budget_cents }) => pb.collection('users').update<User>(uid, { budget_cents }),
    onSettled: (_d, _e, { uid }) => qc.invalidateQueries({ queryKey: meKey(uid) }),
  });
}

/** Whether a change made now goes out right away or waits for the connection. */
export const willQueue = () => !onlineManager.isOnline();

/** Applies a change to the cached list right away and rolls back on error. */
async function optimistic(qc: QueryClient, uid: string, change: (items: Item[]) => Item[]) {
  await qc.cancelQueries({ queryKey: itemsKey(uid) });
  const prev = qc.getQueryData<Item[]>(itemsKey(uid));
  qc.setQueryData<Item[]>(itemsKey(uid), (items) => change(items ?? []));
  return prev;
}

export function useSaveItem(uid: string) {
  const qc = useQueryClient();
  return useMutation<Item, Error, SaveItemVars, Snapshot>({
    mutationKey: saveKey,
    onMutate: ({ id, createId, data }) =>
      optimistic(qc, uid, (items) => {
        // the picture shows once the server has it
        const { image: _file, ...fields } = data;
        void _file;
        if (id) return items.map((i) => (i.id === id ? { ...i, ...fields } : i));
        const draft = {
          image: '',
          ...fields,
          id: createId,
          owner: uid,
          created: new Date().toISOString().replace('T', ' '),
          pending: true,
        } as Item;
        return [draft, ...items];
      }),
    onError: (_e, _v, prev) => qc.setQueryData(itemsKey(uid), prev),
  });
}

export function useDeleteItem(uid: string) {
  const qc = useQueryClient();
  return useMutation<boolean, Error, DeleteItemVars, Snapshot>({
    mutationKey: deleteKey,
    onMutate: ({ id }) => optimistic(qc, uid, (items) => items.filter((i) => i.id !== id)),
    onError: (_e, _v, prev) => qc.setQueryData(itemsKey(uid), prev),
  });
}

export function useSetBudget(uid: string) {
  const qc = useQueryClient();
  return useMutation<User, Error, BudgetVars, User | undefined>({
    mutationKey: budgetKey,
    onMutate: async ({ budget_cents }) => {
      await qc.cancelQueries({ queryKey: meKey(uid) });
      const prev = qc.getQueryData<User>(meKey(uid));
      if (prev) qc.setQueryData<User>(meKey(uid), { ...prev, budget_cents });
      return prev;
    },
    onError: (_e, _v, prev) => qc.setQueryData(meKey(uid), prev),
  });
}

type OutfitSnapshot = Outfit[] | undefined;

async function optimisticOutfits(qc: QueryClient, uid: string, change: (outfits: Outfit[]) => Outfit[]) {
  await qc.cancelQueries({ queryKey: outfitsKey(uid) });
  const prev = qc.getQueryData<Outfit[]>(outfitsKey(uid));
  qc.setQueryData<Outfit[]>(outfitsKey(uid), (outfits) => change(outfits ?? []));
  return prev;
}

export function useSaveOutfit(uid: string) {
  const qc = useQueryClient();
  return useMutation<Outfit, Error, SaveOutfitVars, OutfitSnapshot>({
    mutationKey: saveOutfitKey,
    onMutate: ({ id, createId, data, addItem, removeItem }) =>
      optimisticOutfits(qc, uid, (outfits) => {
        if (id)
          return outfits.map((o) => {
            if (o.id !== id) return o;
            let items = data?.items ?? o.items;
            if (addItem && !items.includes(addItem)) items = [...items, addItem];
            if (removeItem) items = items.filter((i) => i !== removeItem);
            return { ...o, ...data, items };
          });
        const draft: Outfit = {
          name: '',
          note: '',
          ...data,
          items: data?.items ?? (addItem ? [addItem] : []),
          id: createId!,
          owner: uid,
          created: new Date().toISOString().replace('T', ' '),
          updated: '',
          pending: true,
        };
        return [draft, ...outfits];
      }),
    onError: (_e, _v, prev) => qc.setQueryData(outfitsKey(uid), prev),
  });
}

export function useDeleteOutfit(uid: string) {
  const qc = useQueryClient();
  return useMutation<boolean, Error, DeleteOutfitVars, OutfitSnapshot>({
    mutationKey: deleteOutfitKey,
    onMutate: ({ id }) => optimisticOutfits(qc, uid, (outfits) => outfits.filter((o) => o.id !== id)),
    onError: (_e, _v, prev) => qc.setQueryData(outfitsKey(uid), prev),
  });
}

type BoardSnapshot = Board[] | undefined;

const merge = (list: string[], add: string[] = [], remove: string[] = []) =>
  [...list.filter((x) => !remove.includes(x)), ...add.filter((x) => !list.includes(x))];

export function useSaveBoard(uid: string) {
  const qc = useQueryClient();
  return useMutation<Board, Error, SaveBoardVars, BoardSnapshot>({
    mutationKey: saveBoardKey,
    onMutate: async ({ id, createId, name, add, remove }) => {
      await qc.cancelQueries({ queryKey: boardsKey(uid) });
      const prev = qc.getQueryData<Board[]>(boardsKey(uid));
      qc.setQueryData<Board[]>(boardsKey(uid), (boards = []) => {
        if (id)
          return boards.map((b) =>
            b.id === id
              ? {
                  ...b,
                  name: name ?? b.name,
                  items: merge(b.items, add?.items, remove?.items),
                  outfits: merge(b.outfits, add?.outfits, remove?.outfits),
                }
              : b,
          );
        const draft: Board = {
          id: createId!,
          owner: uid,
          name: name ?? '',
          items: add?.items ?? [],
          outfits: add?.outfits ?? [],
          created: new Date().toISOString().replace('T', ' '),
          updated: '',
          pending: true,
        };
        return [draft, ...boards];
      });
      return prev;
    },
    onError: (_e, _v, prev) => qc.setQueryData(boardsKey(uid), prev),
  });
}

export function useDeleteBoard(uid: string) {
  const qc = useQueryClient();
  return useMutation<boolean, Error, DeleteBoardVars, BoardSnapshot>({
    mutationKey: deleteBoardKey,
    onMutate: async ({ id }) => {
      await qc.cancelQueries({ queryKey: boardsKey(uid) });
      const prev = qc.getQueryData<Board[]>(boardsKey(uid));
      qc.setQueryData<Board[]>(boardsKey(uid), (boards = []) => boards.filter((b) => b.id !== id));
      return prev;
    },
    onError: (_e, _v, prev) => qc.setQueryData(boardsKey(uid), prev),
  });
}
